/**
 * Tools Resource
 *
 * Thin 1:1 mapping to the tools/MCP API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import { APIError, SDKError, ShogunChopHTTPError } from '../errors.js';
import type { ShogunChopResource } from './shogunchop.js';
import type { ShogunChopMcpServer } from '../types/shogunchop.js';
import type {
  AddLocalMCPRequest,
  UpdateLocalMCPRequest,
  UpdateLocalMCPResponse,
  LocalMCPServer,
  RegisterMCPRequest,
  RegisterMCPResponse,
  ListMCPsResponse,
  UpdateMCPRequest,
  UpdateMCPResponse,
  DeleteMCPResponse,
  ControlMCPResponse,
  McpControlAction,
  ListAccountMCPsResponse,
  ReconcileMCPEndpoint,
  ReconcileDeviceMcpResult,
  ToolCatalogResponse,
  ListMCPToolsResponse,
  ExecuteToolRequest,
  ExecuteToolResponse,
  ListAvailableToolsResponse,
  ListAvailableSkillsResponse,
} from '../types/index.js';

const NO_LOCAL_COMPUTER_MESSAGE =
  'Local MCP servers are added and edited from the ShogunChop desktop app, on the computer that runs them.';

function localServer(s: ShogunChopMcpServer): LocalMCPServer {
  return {
    id: s.id,
    name: s.name,
    command: s.command,
    args: s.args,
    env_keys: s.env_keys,
    observed_state: s.observed_state,
    tool_names: s.tools.map((t) => t.name),
    ...(s.error ? { error: s.error } : {}),
  };
}

function daemonError(err: unknown): unknown {
  if (!(err instanceof ShogunChopHTTPError)) return err;
  const body = (err.body && typeof err.body === 'object' ? err.body : null) as { code?: unknown; error?: unknown } | null;
  if (typeof body?.code !== 'string' || typeof body.error !== 'string') {
    return new SDKError(
      `ShogunChop daemon answered ${err.status} ${err.statusText} (${err.url}) without the code and error every MCP route failure carries: ` +
        (typeof err.body === 'string' ? err.body : JSON.stringify(err.body)),
    );
  }
  return new APIError(err.status, err.statusText, { code: body.code, detail: body.error });
}

class NoLocalComputerError extends APIError {
  constructor() {
    super(409, 'Conflict', { code: 'no_local_computer', detail: NO_LOCAL_COMPUTER_MESSAGE });
    this.message = NO_LOCAL_COMPUTER_MESSAGE;
  }
}

export class ToolsResource {
  constructor(
    private http: HttpLayer,
    private daemon: () => ShogunChopResource | null,
  ) {}

  private localComputer(): ShogunChopResource {
    const daemon = this.daemon();
    if (!daemon) {
      throw new NoLocalComputerError();
    }
    return daemon;
  }

  /**
   * Install a local MCP server on the user's own computer: the computer the
   * ShogunChop desktop app is running on. This is the way to install a local
   * (stdio) MCP server such as `npx chrome-devtools-mcp@latest`.
   *
   * The computer starts the server, waits for it to answer (up to 2 minutes on
   * a first start, which may download the package), reports it to the cloud and
   * saves it, so it is respawned at startup. `env` values stay on that computer.
   *
   * Works only in the desktop app. Elsewhere it fails with `no_local_computer`
   * and nothing is stored. Failures from the computer carry its code and reason:
   * `duplicate_name`, `invalid_env`, `spawn_failed`, `handshake_failed`,
   * `report_failed`, `persist_failed`, `user_env_unavailable`; a failed add
   * leaves nothing behind. `tools.registerMCP` and
   * `mcp.register` refuse local definitions; use this method instead.
   */
  async addLocalMCP(params: AddLocalMCPRequest): Promise<LocalMCPServer> {
    const daemon = this.localComputer();
    try {
      return localServer(
        await daemon.addMcpServer({ name: params.name, command: params.command, args: params.args ?? [], env: params.env }),
      );
    } catch (err) {
      throw daemonError(err);
    }
  }

  /**
   * Change a local MCP server's command, arguments or environment on the user's
   * own computer. The name cannot change; a rename is a delete and an add.
   *
   * A running server restarts on the new definition. If it fails to start, the
   * new definition is still saved, the server reads `crashed`, and this fails
   * with `edit_failed` and the reason; editing it again with a corrected
   * definition starts it. A stopped server is only saved. `outcome: 'queued'`
   * means the edit waits behind an earlier command for the same server and will
   * apply after it. `outcome: 'superseded'` means the definition was saved but a
   * later command for the server decided its state, so it was not started.
   *
   * Works only in the desktop app (`no_local_computer` elsewhere). Other codes:
   * `not_found`, `rename_not_supported`, `invalid_edit`, `invalid_env`,
   * `persist_failed`, `user_env_unavailable`. `env_set` values stay on that computer.
   */
  async updateLocalMCP(mcpId: string, params: UpdateLocalMCPRequest): Promise<UpdateLocalMCPResponse> {
    const daemon = this.localComputer();
    let reply;
    try {
      reply = await daemon.editMcpServer(mcpId, params);
    } catch (err) {
      throw daemonError(err);
    }
    if ('code' in reply) return { outcome: 'queued', mcp_id: mcpId, message: reply.error };
    return { outcome: reply.outcome, mcp_id: reply.mcp_id, observed_state: reply.observed_state, server: localServer(reply.server) };
  }

  // --- Device-scoped MCP endpoints ---

  private mcpPrefix(deviceId: string): string {
    return `/v1/devices/${deviceId}/mcp`;
  }

  /**
   * Register an MCP server for a device. A remote server (`http`) is stored with
   * its `url`. A local (`bridge`) registration is the computer's own report of a
   * server it already runs, with an empty `config`; local servers are added from
   * the ShogunChop desktop app. A `bridge` registration whose `config` carries
   * `command`, `args` or `env` is refused (`error_code:
   * local_server_definition_refused`, HTTP 422), and one the connected computer
   * says it does not run is refused (`error_code: not_running_on_device`, HTTP
   * 409). A refused registration stores nothing.
   */
  async registerMCP(
    deviceId: string,
    params: RegisterMCPRequest,
  ): Promise<RegisterMCPResponse> {
    return this.http.post(this.mcpPrefix(deviceId), params);
  }

  async listMCPs(deviceId: string): Promise<ListMCPsResponse> {
    return this.http.get(this.mcpPrefix(deviceId));
  }

  async updateMCP(
    deviceId: string,
    mcpId: string,
    params: UpdateMCPRequest,
  ): Promise<UpdateMCPResponse> {
    return this.http.put(
      `${this.mcpPrefix(deviceId)}/${mcpId}`,
      params,
    );
  }

  /**
   * Remove an MCP server from its device and the platform. For a server that
   * runs on a computer, the computer removes it first; while that computer is
   * offline the delete is refused (`error_code: device_offline`) and nothing
   * changes. A later command that keeps the server running refuses the delete
   * with `error_code: overridden`.
   */
  async deleteMCP(
    deviceId: string,
    mcpId: string,
  ): Promise<DeleteMCPResponse> {
    return this.http.delete(
      `${this.mcpPrefix(deviceId)}/${mcpId}`,
      undefined,
      { retry: false },
    );
  }

  /**
   * Start, stop or restart an MCP server on the computer it runs on, without
   * deleting it. The newest command on a server wins: an `overridden` outcome
   * means a later command replaced this one, and `observed_state` is the
   * server's state after it. Fails with `error_code` `device_offline`,
   * `daemon_update_required`, `daemon_error` or `outcome_unknown`.
   */
  async controlMCP(
    deviceId: string,
    mcpId: string,
    action: McpControlAction,
  ): Promise<ControlMCPResponse> {
    return this.http.post(
      `${this.mcpPrefix(deviceId)}/${mcpId}/control`,
      { action },
      { retry: false },
    );
  }

  /**
   * Converge a device's persisted MCP registrations onto the live set its
   * daemon is actually running: adopt the reported servers, reap the rest.
   *
   * `endpoints` is the device's COMPLETE live set, and the empty array is
   * meaningful — it reaps every registration for this device. Omitting the
   * field is a 4xx, never an implicit reap-all, so a caller that failed to
   * enumerate its servers can never be read as a caller with none.
   *
   * The server owns `scope`/`location` (derived from `transport`) and pins
   * `device_id` to the path; those fields may be sent, but a value that
   * disagrees is rejected by name rather than silently corrected. `enabled` and
   * `description` are platform-owned and are never written by a reconcile, so
   * an admin-disabled server stays disabled across daemon restarts.
   *
   * Not agent-reachable: it is excluded from the `platform_call` dispatch
   * allowlist. The reconcile carries a daemon-authoritative live set with a
   * whole-device reap, and an agent has no live set to report — a dispatchable
   * reconcile could only ever destroy registrations.
   */
  async reconcileDeviceMcp(
    deviceId: string,
    endpoints: ReconcileMCPEndpoint[],
  ): Promise<ReconcileDeviceMcpResult> {
    return this.http.post(`${this.mcpPrefix(deviceId)}/reconcile`, { endpoints });
  }

  async getToolCatalog(
    deviceId: string,
  ): Promise<ToolCatalogResponse> {
    return this.http.get(`${this.mcpPrefix(deviceId)}/catalog`);
  }

  async listMCPTools(
    deviceId: string,
    mcpId: string,
  ): Promise<ListMCPToolsResponse> {
    return this.http.get(
      `${this.mcpPrefix(deviceId)}/${mcpId}/tools`,
    );
  }

  // --- Tenant-scoped tool endpoints ---

  async listTenantMCPs(): Promise<ListMCPsResponse> {
    return this.http.get('/v1/tools/mcps');
  }

  /**
   * Every MCP server that runs on one of the account's computers, with the
   * computer's id and name and the server's live `observed_state`.
   */
  async listAccountMCPs(): Promise<ListAccountMCPsResponse> {
    return this.http.get('/v1/tools/device-mcps');
  }

  async listTenantMCPTools(
    mcpId: string,
  ): Promise<ListMCPToolsResponse> {
    return this.http.get(`/v1/tools/mcps/${mcpId}/tools`);
  }

  async getTenantToolCatalog(): Promise<ToolCatalogResponse> {
    return this.http.get('/v1/tools/catalog');
  }

  // --- Tool execution ---

  async listAvailableTools(
    workspaceId?: string | null,
    deviceId?: string,
  ): Promise<ListAvailableToolsResponse> {
    const path = workspaceId
      ? `/v1/tools/${workspaceId}/available`
      : `/v1/tools/available`;
    return this.http.get(path, { device_id: deviceId });
  }

  async executeTool(
    workspaceId: string | null | undefined,
    params: ExecuteToolRequest,
  ): Promise<ExecuteToolResponse> {
    const path = workspaceId
      ? `/v1/tools/${workspaceId}/execute`
      : `/v1/tools/execute`;
    return this.http.post(path, params);
  }

  /**
   * Discover the workspace's `.shogunchop/skills/` index. A thin read mirroring listAvailableTools; the palette merges
   * these skill-kind capabilities with listAvailableTools' coded tools.
   * `deviceId` selects the local (daemon) substrate for workspaces with no
   * cloud repository — same optional threading as listAvailableTools.
   */
  async listAvailableSkills(
    workspaceId: string,
    deviceId?: string,
  ): Promise<ListAvailableSkillsResponse> {
    return this.http.get(`/v1/tools/${workspaceId}/skills`, { device_id: deviceId });
  }
}
